'use strict';

import React, { Component } from 'react';
import Highcharts from 'highcharts';
import highchartsDrilldown from 'highcharts/modules/drilldown';
import HighchartsReact from 'highcharts-react-official';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import moment from 'moment';
import redirectURL from '../redirectURL';
import {
  COLORS,
  MULTI_SELECT_STYLES,
  safeArray,
  groupBy,
  distinctValues,
  sum,
  round1,
  isDeliveredRow,
  baseChartOptions,
  baseValueAxis,
  buildPivot,
  GridCard,
  DrilldownChartCard,
  percentValueFormatter,
} from './dashboardShared';
import './performanceDashboardModern.css';

highchartsDrilldown(Highcharts);

/*
 * Transporter Dashboard - a second tab alongside the Performance Dashboard,
 * sharing the same GridCard/chart primitives (imported from
 * dashboardShared.js) rather than forking new ones.
 *
 * LR Wise reads consignments/getDataForPowerBiDashBoard, same as the
 * Performance Dashboard (per-LR rows, all filters except the date range
 * applied client-side).
 *
 * Invoice Wise reads consignments/getInvoiceData - a genuinely
 * invoice-level feed (one row per invoice, confirmed by real sample data:
 * two rows sharing the same LR/shipment but distinct Invoice Nos), NOT
 * derived from the LR feed. This endpoint originally took no date
 * parameter at all and returned its entire unbounded history (crashing
 * past the 25MB proxy safety cap - see proxyMlEndpointWithSizeCap in
 * consignmentsController.js); the backend has since added
 * startDate/endDate/date_filter support, mirroring getDataForPowerBiDashBoard.
 * Its response uses a different, inconsistent key naming convention
 * ("Invoice No", "Destination Zone", but snake_case dispatch_date/
 * invoice_time) - normalizeInvoiceRow maps it to the same internal field
 * names used elsewhere on this dashboard. It also has NO weight field and
 * is missing Entity/Source Zone/Destination City/Destination State/Fuel
 * Type - so Invoice Wise's bar widgets have no weight measure toggle
 * (Invoice Count only), and applyInvoiceClientFilters only applies the
 * filters whose fields actually exist in this feed.
 *
 * Weight Wise still renders an honest placeholder until its exact
 * charts/metrics are specified, the same pattern used earlier for CPD
 * before that field existed.
 */

const CATEGORICAL_ORDER = [
  COLORS.blue, COLORS.orange, COLORS.aqua, COLORS.yellow,
  COLORS.magenta, COLORS.green, COLORS.violet, COLORS.red,
];

// Fixed, name-based colors (not positional) so a category always renders the
// same color regardless of what order it happens to appear in the data -
// matches Power BI's stable palette (FTL/API always the same color across
// reloads and date ranges, confirmed against the live report).
const CATEGORY_COLOR_MAP = {
  Primary: COLORS.blue,
  Secondary: COLORS.aqua,
  FTL: COLORS.blue,
  PTL: COLORS.orange,
  API: COLORS.green,
  'Manual Upload': COLORS.red,
  'SIM Tracking': COLORS.yellow,
  'Sim Tracking': COLORS.yellow,
  Other: COLORS.violet,
  Organized: COLORS.green,
  Unorganized: COLORS.orange,
  Unspecified: COLORS.muted,
};

function colorForCategory(name, idx) {
  return CATEGORY_COLOR_MAP[name] || CATEGORICAL_ORDER[idx % CATEGORICAL_ORDER.length];
}

const DATE_TYPE_OPTIONS = [
  { value: 'Dispatch', label: 'Dispatch Date' },
  { value: 'Invoice', label: 'Invoice Date' },
];

// "Mode" here = tracking_type (FTL/PTL), matching the LR-Wise "Mode
// Distribution" widget's own definition, not shipment_mode.
const MODE_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'FTL', label: 'FTL' },
  { value: 'PTL', label: 'PTL' },
];

const DATA_VIEW_TABS = [
  { key: 'lr', label: "LR Wise" },
  { key: 'invoice', label: 'Invoice Wise' },
  { key: 'weight', label: 'Weight Wise' },
];

const LR_MEASURE_OPTIONS = [
  { key: 'lrCount', label: 'LR Count' },
  { key: 'weightTons', label: 'Total Weight (Tons)' },
];

function dispatchDateOf(row, dateType) {
  const value = dateType === 'Invoice' ? row.invoice_time : (row.dispatch_date || row.invoice_time);
  return value ? moment(value) : null;
}

function monthKeyOf(m) {
  return m && m.isValid() ? m.format('MMM YYYY') : null;
}

function monthRange(start, end) {
  const months = [];
  let cursor = moment(start).startOf('month');
  const last = moment(end).startOf('month');
  while (cursor.isSameOrBefore(last)) {
    months.push(cursor.format('MMM YYYY'));
    cursor = cursor.add(1, 'month');
  }
  return months;
}

// Transporter Type = IF(tpt_category = "API", "Organized", "Unorganized") -
// the exact formula from the original Power BI DAX.
function isOrganized(row) {
  return (row.tpt_category || '').toLowerCase() === 'api';
}

// Total Weight (Tons) = lr_total_weight / 1000, per the original DAX measure.
function weightTonsOf(row) {
  const n = Number(row.lr_total_weight);
  return isNaN(n) ? 0 : n / 1000;
}

// consignments/getInvoiceData returns one row per invoice, but with an
// inconsistent key naming convention (Title Case with spaces for most
// fields, snake_case for dispatch_date/invoice_time/shipment_id). Normalize
// to the same internal field names used by the LR feed so the shared
// isOrganized/dispatchDateOf/monthKeyOf helpers all work unchanged.
function normalizeInvoiceRow(row) {
  return {
    invoice_no: row['Invoice No'],
    consignee_zone: row['Destination Zone'],
    consigner_city: row['Source City'],
    dispatch_date: row.dispatch_date,
    invoice_time: row.invoice_time,
    invoice_type: row['Invoice Type'],
    lr_no: row['LR No'],
    shipment_id: row.shipment_id,
    consignment_status: row['Status'],
    tpt_category: row['Transporter Category'],
    tracking_type: row['Tracking Type'],
    transporter_name: row['Transporter Name'],
    final_eta_dt: row['Customer Promise Date'],
    reason_for_delay: row['Late Delivery Remarks'],
  };
}

// getInvoiceData has no numeric status code or delivery_status field like
// the LR feed does - just a plain "Status" string.
function isInvoiceDelivered(row) {
  const status = (row.consignment_status || '').toLowerCase();
  return status === 'delivered' || status === 'trip completed';
}

export default class TransporterDashboardModern extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: null,
      lastRefreshed: null,
      activeDataView: 'lr',
      sourceCityMeasure: 'lrCount',
      destZoneMeasure: 'lrCount',
      // Cross-filter for LR Wise / Weight Wise, set by clicking a KPI card:
      // 'all' scopes widgets 1-4 & 6 to every status, 'delivered' scopes them
      // to delivered-only. Widget 5 (Delivered - Destination Zone) and the
      // two Dispatched/Delivered KPI totals stay fixed regardless, so they
      // remain a stable comparison pair instead of collapsing to one number.
      statusScope: 'all',

      invoiceLoading: true,
      invoiceError: null,
      invoiceRawRows: [],
      invoiceFilteredRows: [],
      invoiceDerived: null,

      filters: {
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
        dateType: DATE_TYPE_OPTIONS[0],
        mode: MODE_OPTIONS[0],
        entity: [],
        movementType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        destinationCity: [],
        destinationState: [],
        fuelType: [],
        transporterCategory: [],
        invoiceType: [],
        transporter: [],
      },

      filterOptions: {
        entity: [],
        movementType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        destinationCity: [],
        destinationState: [],
        fuelType: [],
        transporterCategory: [],
        invoiceType: [],
        transporter: [],
      },

      rawRows: [],
      filteredRows: [],
      derived: null,
    };
  }

  componentDidMount() {
    this.loadData();
    this.loadInvoiceData();
  }

  buildPayload() {
    const f = this.state.filters;
    return {
      startDate: moment(f.startDate).format('YYYY-MM-DD'),
      endDate: moment(f.endDate).format('YYYY-MM-DD'),
      // Confirmed from the actual Flask source: getDataForPowerBiDashBoard
      // takes the same short "dispatch"/"invoice" tokens as getInvoiceData
      // below, not the literal field names - sending the literal names
      // silently failed the backend's own token check and left date_filter
      // as None every time.
      date_filter: f.dateType.value === 'Invoice' ? 'invoice' : 'dispatch',
    };
  }

  loadData = () => {
    this.setState({ loading: true, error: null });
    redirectURL.post('consignments/getDataForPowerBiDashBoard', this.buildPayload())
      .then((resp) => {
        const rows = safeArray(resp.data);
        rows.forEach((row) => {
          row.movement_type_derived = /stock transfer/i.test(row.invoice_type || '') ? 'Primary' : 'Secondary';
        });

        const filterOptions = {
          entity: distinctValues(rows, 'truck_seal_entity').map((v) => ({ value: v, label: v })),
          movementType: distinctValues(rows, 'movement_type_derived').map((v) => ({ value: v, label: v })),
          sourceZone: distinctValues(rows, 'consigner_zone').map((v) => ({ value: v, label: v })),
          sourceCity: distinctValues(rows, 'consigner_city').map((v) => ({ value: v, label: v })),
          destinationZone: distinctValues(rows, 'consignee_zone').map((v) => ({ value: v, label: v })),
          destinationCity: distinctValues(rows, 'consignee_city').map((v) => ({ value: v, label: v })),
          destinationState: distinctValues(rows, 'consignee_state').map((v) => ({ value: v, label: v })),
          fuelType: distinctValues(rows, 'fuel_type').map((v) => ({ value: v, label: v })),
          transporterCategory: distinctValues(rows, 'tpt_category').map((v) => ({ value: v, label: v })),
          invoiceType: distinctValues(rows, 'invoice_type').map((v) => ({ value: v, label: v })),
          transporter: distinctValues(rows, 'transporter_name').map((v) => ({ value: v, label: v })),
        };

        this.setState((prev) => ({
          loading: false,
          lastRefreshed: moment(),
          rawRows: rows,
          filterOptions: { ...prev.filterOptions, ...filterOptions },
        }));
        this.applyClientFilters(rows);
      })
      .catch((error) => {
        console.error('Error fetching transporter dashboard data:', error);
        this.setState({ loading: false, error: 'Unable to load transporter dashboard data. Please try again.' });
      });
  };

  applyClientFilters = (rowsOverride) => {
    const rows = rowsOverride || this.state.rawRows;
    const f = this.state.filters;
    const matches = (row, selection, field) => {
      if (!selection.length) return true;
      return selection.map((o) => o.value).includes(row[field]);
    };
    const filtered = rows.filter((row) => (
      matches(row, f.entity, 'truck_seal_entity')
      && matches(row, f.movementType, 'movement_type_derived')
      && matches(row, f.sourceZone, 'consigner_zone')
      && matches(row, f.sourceCity, 'consigner_city')
      && matches(row, f.destinationZone, 'consignee_zone')
      && matches(row, f.destinationCity, 'consignee_city')
      && matches(row, f.destinationState, 'consignee_state')
      && matches(row, f.fuelType, 'fuel_type')
      && matches(row, f.transporterCategory, 'tpt_category')
      && matches(row, f.invoiceType, 'invoice_type')
      && matches(row, f.transporter, 'transporter_name')
      && (f.mode.value === 'All' || row.tracking_type === f.mode.value)
    ));
    this.setState({ filteredRows: filtered, derived: this.computeDerived(filtered, this.state.statusScope) });
  };

  setStatusScope = (scope) => {
    this.setState({ statusScope: scope }, () => this.applyClientFilters());
  };

  computeDerived(rows, statusScope = 'all') {
    const totalLR = rows.length;
    const delivered = rows.filter(isDeliveredRow);

    // 1. Distribution of Movement Type (Primary/Secondary)
    const movementGroups = groupBy(rows, (r) => r.movement_type_derived || 'Secondary');
    const movementTypeDist = Array.from(movementGroups.entries())
      .map(([name, items]) => ({ name, count: items.length, pct: totalLR ? round1((items.length / totalLR) * 100) : 0 }));

    // 2. Mode Distribution (FTL/PTL)
    const modeGroups = groupBy(rows, (r) => r.tracking_type || 'Unspecified');
    const modeDist = Array.from(modeGroups.entries())
      .map(([name, items]) => ({ name, count: items.length, pct: totalLR ? round1((items.length / totalLR) * 100) : 0 }));

    // 3. Transporter Category Distribution (API / Manual Upload / SIM Tracking / ...)
    const catGroups = groupBy(rows, (r) => r.tpt_category || 'Unspecified');
    const categoryDist = Array.from(catGroups.entries())
      .map(([name, items]) => ({
        name,
        count: items.length,
        transporterCount: new Set(items.map((r) => r.transporter_name).filter(Boolean)).size,
        pct: totalLR ? round1((items.length / totalLR) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count);

    // 4. LR Dispatched - Source City (+ drilldown to Transporter)
    const cityGroups = groupBy(rows, (r) => r.consigner_city || 'Unspecified');
    const sourceCityRows = Array.from(cityGroups.entries())
      .map(([name, items]) => ({ name, lrCount: items.length, weightTons: round1(sum(items.map(weightTonsOf))) }))
      .sort((a, b) => b.lrCount - a.lrCount);
    const sourceCityDrilldown = {};
    cityGroups.forEach((items, city) => {
      const byTransporter = groupBy(items, (r) => r.transporter_name || 'Unspecified');
      sourceCityDrilldown[city] = Array.from(byTransporter.entries())
        .map(([t, its]) => ({ name: t, lrCount: its.length, weightTons: round1(sum(its.map(weightTonsOf))) }))
        .sort((a, b) => b.lrCount - a.lrCount);
    });

    // 5. LR Delivered - Destination Zone (+ drilldown to Transporter)
    const zoneGroups = groupBy(delivered, (r) => r.consignee_zone || 'Unspecified');
    const destZoneRows = Array.from(zoneGroups.entries())
      .map(([name, items]) => ({ name, lrCount: items.length, weightTons: round1(sum(items.map(weightTonsOf))) }))
      .sort((a, b) => b.lrCount - a.lrCount);
    const destZoneDrilldown = {};
    zoneGroups.forEach((items, zone) => {
      const byTransporter = groupBy(items, (r) => r.transporter_name || 'Unspecified');
      destZoneDrilldown[zone] = Array.from(byTransporter.entries())
        .map(([t, its]) => ({ name: t, lrCount: its.length, weightTons: round1(sum(its.map(weightTonsOf))) }))
        .sort((a, b) => b.lrCount - a.lrCount);
    });

    // 6. LR Wise Share of Business (Organized/Unorganized x month)
    const months = monthRange(this.state.filters.startDate, this.state.filters.endDate);
    const typeOf = (r) => (isOrganized(r) ? 'Organized' : 'Unorganized');
    const sharePivot = buildPivot(
      rows,
      typeOf,
      months,
      (r) => monthKeyOf(dispatchDateOf(r, this.state.filters.dateType.value)),
    );
    const shareRowTotals = {};
    ['Organized', 'Unorganized'].forEach((type) => {
      shareRowTotals[type] = sum(months.map((m) => sharePivot.cell(type, m).length));
    });
    const shareColTotals = {};
    months.forEach((m) => {
      shareColTotals[m] = sum(['Organized', 'Unorganized'].map((type) => sharePivot.cell(type, m).length));
    });
    const shareGrandTotal = sum(Object.values(shareRowTotals));

    // ---- Weight Wise: its own, independently-scoped versions of widgets
    // 1-4 & 6 - clicking a KPI card on Weight Wise only (setStatusScope)
    // re-groups THESE from weightScopedRows, without touching LR Wise's
    // movementGroups/cityGroups/etc. above, which always stay unscoped.
    // Widget 5 (Delivered - Destination Zone) is shared as-is (destZoneRows
    // above) since it's already fixed to delivered-only for both tabs.
    const totalWeightTons = round1(sum(rows.map(weightTonsOf)));
    // Matches the PDF's DeliveredWeights measure exactly: CALCULATE(SUM(weight),
    // Status="Delivered") - a distinct, narrower figure from totalWeightTons
    // above (which sums every status: In Transit, Undelivered, Dispatched
    // from Source, etc.). Power BI's own Total Weight KPI is this delivered-
    // only figure, not the all-statuses total.
    const totalWeightDeliveredTons = round1(sum(delivered.map(weightTonsOf)));
    const weightScopedRows = statusScope === 'delivered' ? delivered : rows;
    const totalWeightScoped = round1(sum(weightScopedRows.map(weightTonsOf)));

    const weightMovementGroups = groupBy(weightScopedRows, (r) => r.movement_type_derived || 'Secondary');
    const weightMovementTypeDist = Array.from(weightMovementGroups.entries())
      .map(([name, items]) => {
        const weightTons = round1(sum(items.map(weightTonsOf)));
        return { name, count: weightTons, pct: totalWeightScoped ? round1((weightTons / totalWeightScoped) * 100) : 0 };
      });
    const weightModeGroups = groupBy(weightScopedRows, (r) => r.tracking_type || 'Unspecified');
    const weightModeDist = Array.from(weightModeGroups.entries())
      .map(([name, items]) => {
        const weightTons = round1(sum(items.map(weightTonsOf)));
        return { name, count: weightTons, pct: totalWeightScoped ? round1((weightTons / totalWeightScoped) * 100) : 0 };
      });
    const weightCatGroups = groupBy(weightScopedRows, (r) => r.tpt_category || 'Unspecified');
    const weightCategoryDist = Array.from(weightCatGroups.entries())
      .map(([name, items]) => {
        const weightTons = round1(sum(items.map(weightTonsOf)));
        return {
          name,
          count: weightTons,
          transporterCount: new Set(items.map((r) => r.transporter_name).filter(Boolean)).size,
          pct: totalWeightScoped ? round1((weightTons / totalWeightScoped) * 100) : 0,
        };
      })
      .sort((a, b) => b.count - a.count);

    const weightCityGroups = groupBy(weightScopedRows, (r) => r.consigner_city || 'Unspecified');
    const weightSourceCityRows = Array.from(weightCityGroups.entries())
      .map(([name, items]) => ({ name, lrCount: items.length, weightTons: round1(sum(items.map(weightTonsOf))) }))
      .sort((a, b) => b.weightTons - a.weightTons);
    const weightSourceCityDrilldown = {};
    weightCityGroups.forEach((items, city) => {
      const byTransporter = groupBy(items, (r) => r.transporter_name || 'Unspecified');
      weightSourceCityDrilldown[city] = Array.from(byTransporter.entries())
        .map(([t, its]) => ({ name: t, lrCount: its.length, weightTons: round1(sum(its.map(weightTonsOf))) }))
        .sort((a, b) => b.weightTons - a.weightTons);
    });

    const weightSharePivot = buildPivot(
      weightScopedRows,
      typeOf,
      months,
      (r) => monthKeyOf(dispatchDateOf(r, this.state.filters.dateType.value)),
    );
    const weightShareRowTotals = {};
    ['Organized', 'Unorganized'].forEach((type) => {
      weightShareRowTotals[type] = round1(sum(months.map((m) => sum(weightSharePivot.cell(type, m).map(weightTonsOf)))));
    });
    const weightShareColTotals = {};
    months.forEach((m) => {
      weightShareColTotals[m] = round1(sum(['Organized', 'Unorganized'].map((type) => sum(weightSharePivot.cell(type, m).map(weightTonsOf)))));
    });
    const weightShareGrandTotal = round1(sum(Object.values(weightShareRowTotals)));

    return {
      totalLR,
      movementTypeDist,
      modeDist,
      categoryDist,
      sourceCityRows,
      sourceCityDrilldown,
      destZoneRows,
      destZoneDrilldown,
      shareOfBusiness: { months, cell: sharePivot.cell, rowTotals: shareRowTotals, colTotals: shareColTotals, grandTotal: shareGrandTotal },
      totalWeightTons,
      totalWeightDeliveredTons,
      statusScope,
      weightSourceCityRows,
      weightSourceCityDrilldown,
      weightMovementTypeDist,
      weightModeDist,
      weightCategoryDist,
      weightShareOfBusiness: { months, cell: weightSharePivot.cell, rowTotals: weightShareRowTotals, colTotals: weightShareColTotals, grandTotal: weightShareGrandTotal },
    };
  }

  // Invoice Wise: consignments/getInvoiceData is fetched separately (see
  // loadInvoiceData) since it's a genuinely different, invoice-level feed -
  // not derived from the LR rows above. No weight measure (the feed has no
  // weight field at all).
  loadInvoiceData = () => {
    const f = this.state.filters;
    this.setState({ invoiceLoading: true, invoiceError: null });
    redirectURL.post('consignments/getInvoiceData', {
      startDate: moment(f.startDate).format('YYYY-MM-DD'),
      endDate: moment(f.endDate).format('YYYY-MM-DD'),
      // Assumed counterpart to the confirmed-working "invoice" value -
      // unverified against the backend, flag if Dispatch Date basis looks
      // unfiltered on this tab.
      date_filter: f.dateType.value === 'Invoice' ? 'invoice' : 'dispatch',
    })
      .then((resp) => {
        const rows = safeArray(resp.data).map(normalizeInvoiceRow);
        rows.forEach((row) => {
          row.movement_type_derived = /stock transfer/i.test(row.invoice_type || '') ? 'Primary' : 'Secondary';
        });
        this.setState({ invoiceLoading: false, invoiceRawRows: rows });
        this.applyInvoiceClientFilters(rows);
      })
      .catch((error) => {
        console.error('Error fetching invoice-wise data:', error);
        this.setState({ invoiceLoading: false, invoiceError: 'Unable to load invoice-wise data. Please try again.' });
      });
  };

  // Only applies filters whose fields actually exist on the invoice feed -
  // Entity/Source Zone/Destination City/Destination State/Fuel Type are
  // silently skipped rather than matched against a field that's always
  // undefined here (which would wrongly filter out every row the moment a
  // user picked any value for one of those, even though the filter bar is
  // shared with LR Wise where those fields do exist).
  applyInvoiceClientFilters = (rowsOverride) => {
    const rows = rowsOverride || this.state.invoiceRawRows;
    const f = this.state.filters;
    const matches = (row, selection, field) => {
      if (!selection.length) return true;
      return selection.map((o) => o.value).includes(row[field]);
    };
    const filtered = rows.filter((row) => (
      matches(row, f.movementType, 'movement_type_derived')
      && matches(row, f.sourceCity, 'consigner_city')
      && matches(row, f.destinationZone, 'consignee_zone')
      && matches(row, f.transporterCategory, 'tpt_category')
      && matches(row, f.invoiceType, 'invoice_type')
      && matches(row, f.transporter, 'transporter_name')
      && (f.mode.value === 'All' || row.tracking_type === f.mode.value)
    ));
    this.setState({ invoiceFilteredRows: filtered, invoiceDerived: this.computeInvoiceDerived(filtered) });
  };

  computeInvoiceDerived(rows) {
    const totalInvoices = rows.length;

    const movementGroups = groupBy(rows, (r) => r.movement_type_derived || 'Secondary');
    const invoiceMovementTypeDist = Array.from(movementGroups.entries())
      .map(([name, items]) => ({ name, count: items.length, pct: totalInvoices ? round1((items.length / totalInvoices) * 100) : 0 }));

    const modeGroups = groupBy(rows, (r) => r.tracking_type || 'Unspecified');
    const invoiceModeDist = Array.from(modeGroups.entries())
      .map(([name, items]) => ({ name, count: items.length, pct: totalInvoices ? round1((items.length / totalInvoices) * 100) : 0 }));

    const catGroups = groupBy(rows, (r) => r.tpt_category || 'Unspecified');
    const invoiceCategoryDist = Array.from(catGroups.entries())
      .map(([name, items]) => ({
        name,
        count: items.length,
        transporterCount: new Set(items.map((r) => r.transporter_name).filter(Boolean)).size,
        pct: totalInvoices ? round1((items.length / totalInvoices) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count);

    const cityGroups = groupBy(rows, (r) => r.consigner_city || 'Unspecified');
    const invoiceSourceCityRows = Array.from(cityGroups.entries())
      .map(([name, items]) => ({ name, invoiceCount: items.length }))
      .sort((a, b) => b.invoiceCount - a.invoiceCount);
    const invoiceSourceCityDrilldown = {};
    cityGroups.forEach((items, city) => {
      const byTransporter = groupBy(items, (r) => r.transporter_name || 'Unspecified');
      invoiceSourceCityDrilldown[city] = Array.from(byTransporter.entries())
        .map(([t, its]) => ({ name: t, invoiceCount: its.length }))
        .sort((a, b) => b.invoiceCount - a.invoiceCount);
    });

    const delivered = rows.filter(isInvoiceDelivered);
    const zoneGroups = groupBy(delivered, (r) => r.consignee_zone || 'Unspecified');
    const invoiceDestZoneRows = Array.from(zoneGroups.entries())
      .map(([name, items]) => ({ name, invoiceCount: items.length }))
      .sort((a, b) => b.invoiceCount - a.invoiceCount);
    const invoiceDestZoneDrilldown = {};
    zoneGroups.forEach((items, zone) => {
      const byTransporter = groupBy(items, (r) => r.transporter_name || 'Unspecified');
      invoiceDestZoneDrilldown[zone] = Array.from(byTransporter.entries())
        .map(([t, its]) => ({ name: t, invoiceCount: its.length }))
        .sort((a, b) => b.invoiceCount - a.invoiceCount);
    });

    const months = monthRange(this.state.filters.startDate, this.state.filters.endDate);
    const typeOf = (r) => (isOrganized(r) ? 'Organized' : 'Unorganized');
    const sharePivot = buildPivot(
      rows,
      typeOf,
      months,
      (r) => monthKeyOf(dispatchDateOf(r, this.state.filters.dateType.value)),
    );
    const shareRowTotals = {};
    ['Organized', 'Unorganized'].forEach((type) => {
      shareRowTotals[type] = sum(months.map((m) => sharePivot.cell(type, m).length));
    });
    const shareColTotals = {};
    months.forEach((m) => {
      shareColTotals[m] = sum(['Organized', 'Unorganized'].map((type) => sharePivot.cell(type, m).length));
    });
    const shareGrandTotal = sum(Object.values(shareRowTotals));

    return {
      totalInvoices,
      invoiceMovementTypeDist,
      invoiceModeDist,
      invoiceCategoryDist,
      invoiceSourceCityRows,
      invoiceSourceCityDrilldown,
      invoiceDestZoneRows,
      invoiceDestZoneDrilldown,
      invoiceShareOfBusiness: { months, cell: sharePivot.cell, rowTotals: shareRowTotals, colTotals: shareColTotals, grandTotal: shareGrandTotal },
    };
  }

  onFilterChange = (key, value) => {
    this.setState((prev) => ({ filters: { ...prev.filters, [key]: value } }));
  };

  onApply = () => { this.loadData(); this.loadInvoiceData(); };

  onReset = () => {
    this.setState({
      filters: {
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
        dateType: DATE_TYPE_OPTIONS[0],
        mode: MODE_OPTIONS[0],
        entity: [],
        movementType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        destinationCity: [],
        destinationState: [],
        fuelType: [],
        transporterCategory: [],
        invoiceType: [],
        transporter: [],
      },
      statusScope: 'all',
    }, () => { this.loadData(); this.loadInvoiceData(); });
  };

  renderEmptyState(message, hint) {
    return (
      <div className="pdm-empty-state">
        <div className="pdm-empty-state-icon">&#9679;</div>
        <strong>{message}</strong>
        {hint && <span>{hint}</span>}
      </div>
    );
  }

  renderChartCard(title, subtitle, options, height = 320) {
    return (
      <div className="pdm-card">
        {title && <p className="pdm-card-title">{title}</p>}
        {subtitle && <p className="pdm-card-subtitle">{subtitle}</p>}
        {options ? (
          <div className="pdm-chart-flex" style={{ height }}>
            <HighchartsReact
              highcharts={Highcharts}
              containerProps={{ style: { width: '100%', height: '100%' } }}
              options={{ ...options, chart: { ...options.chart, height } }}
              updateArgs={[true, true, true]}
            />
          </div>
        ) : this.renderEmptyState('No data available', 'Try widening the date range or clearing a filter.')}
      </div>
    );
  }

  renderDonutWithLegend(title, subtitle, items, buildOptions, legendRenderer, height = 300) {
    return (
      <div className="pdm-card">
        <p className="pdm-card-title">{title}</p>
        <p className="pdm-card-subtitle">{subtitle}</p>
        {items.length ? (
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px', minWidth: 200 }}>
              <HighchartsReact highcharts={Highcharts} options={{ ...buildOptions(items), chart: { ...buildOptions(items).chart, height } }} updateArgs={[true, true, true]} />
            </div>
            <div style={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {items.map((item, idx) => legendRenderer(item, colorForCategory(item.name, idx)))}
            </div>
          </div>
        ) : this.renderEmptyState('No data available', null)}
      </div>
    );
  }

  buildDonutOptions(items, unitLabel = 'Total LRs', pointUnit = 'LRs') {
    const total = round1(sum(items.map((item) => item.count)));
    return {
      ...baseChartOptions(),
      chart: { ...baseChartOptions().chart, type: 'pie' },
      title: {
        useHTML: true,
        text: `<div style="text-align:center;line-height:1.2"><div style="font-size:22px;font-weight:700;color:${COLORS.ink}">${total}</div><div style="font-size:11px;color:${COLORS.muted}">${unitLabel}</div></div>`,
        align: 'center',
        verticalAlign: 'middle',
        y: 6,
      },
      tooltip: { pointFormat: `<b>{point.y}</b> ${pointUnit} ({point.percentage:.1f}%)` },
      legend: { enabled: false },
      plotOptions: { pie: { innerSize: '65%', borderWidth: 2, borderColor: '#fff', dataLabels: { enabled: false } } },
      series: [{
        name: pointUnit,
        data: items.map((item, idx) => ({ name: item.name, y: item.count, color: colorForCategory(item.name, idx) })),
      }],
    };
  }

  buildDrilldownBarOptions(rows, drilldownMap, measureKey, yTitle) {
    if (!rows.length) return null;
    const drilldownSeries = Object.entries(drilldownMap).map(([key, items]) => ({
      id: key,
      name: `${key} - by Transporter`,
      data: items.map((it) => ({ name: it.name, y: it[measureKey] })),
    }));
    return {
      ...baseChartOptions(),
      title: { text: null },
      // Deliberately NOT a static `categories` array (unlike baseAxisLine,
      // used elsewhere for non-drilldown charts): Highcharts' drilldown
      // module only re-labels the axis from each new series' own point
      // names when the axis manages categories dynamically. A fixed
      // categories array stays put across a drill, so the new (transporter)
      // bars would render under the old (city/zone) labels positionally.
      xAxis: {
        type: 'category',
        lineColor: '#c3c2b7',
        tickColor: '#c3c2b7',
        // Styled to always look clickable (blue + underline + pointer),
        // unconditionally on every label - not relying on Highcharts' own
        // "active label" detection, which only flagged a handful of labels
        // as active in practice (matching points to labels by tick
        // position, which isn't reliable with this many dynamic
        // categories). The actual click binding below is equally
        // independent of that detection.
        labels: { style: { color: COLORS.blue, fontSize: '11px', textDecoration: 'underline', cursor: 'pointer' } },
      },
      yAxis: baseValueAxis(yTitle),
      legend: { enabled: false },
      plotOptions: {
        series: {
          borderRadius: 4,
          borderWidth: 0,
          colorByPoint: true,
          cursor: 'pointer',
          // Highcharts dims non-hovered points/labels to ~20% opacity by
          // default whenever any point in the series is hovered - reads as
          // city names randomly "turning gray". Every bar is independently
          // clickable here, so there's no reason to dim the others; keep
          // full opacity regardless of hover state.
          states: { inactive: { opacity: 1 } },
          dataLabels: { enabled: true, style: { fontSize: '10px', color: COLORS.ink, textOutline: 'none' } },
          // doDrilldown() called directly on point click, bypassing
          // Highcharts' automatic click-to-drilldown wiring so the bar is
          // always a reliable click target regardless of any axis quirk.
          point: {
            events: {
              click: function () {
                if (this.drilldown) this.doDrilldown();
              },
            },
          },
        },
      },
      // Makes clicking the axis LABEL text itself open the same drilldown
      // as clicking its bar - done manually here (matching each tick to its
      // point by array position, which is reliable at the top level before
      // any drill) instead of trusting Highcharts' own built-in axis-label
      // click wiring, which only worked for a handful of labels in
      // practice. Re-runs on every redraw (drill in/out), and simply does
      // nothing once drilled into a transporter breakdown, since those
      // points have no `drilldown` id.
      chart: {
        ...baseChartOptions().chart,
        type: 'column',
        events: {
          render: function () {
            const axis = this.xAxis && this.xAxis[0];
            const series = this.series && this.series[0];
            if (!axis || !series || !axis.ticks) return;
            Object.keys(axis.ticks).forEach((pos) => {
              const tick = axis.ticks[pos];
              const point = series.data[Number(pos)];
              if (!tick || !tick.label || !tick.label.element) return;
              tick.label.element.style.cursor = point && point.drilldown ? 'pointer' : '';
              tick.label.element.onclick = function () {
                if (point && point.drilldown) point.doDrilldown();
              };
            });
          },
        },
      },
      tooltip: { headerFormat: '', pointFormat: `<b>{point.key}</b><br/>${yTitle}: <b>{point.y}</b>` },
      series: [{
        name: yTitle,
        data: rows.map((r, idx) => ({ name: r.name, y: r[measureKey], drilldown: r.name, color: CATEGORICAL_ORDER[idx % CATEGORICAL_ORDER.length] })),
      }],
      drilldown: { series: drilldownSeries },
    };
  }

  renderMeasureToggle(options, current, onChange) {
    return (
      <div className="pdm-mode-toggle">
        {options.map((m) => (
          <button key={m.key} className={`pdm-chip ${current === m.key ? 'pdm-chip-active' : ''}`} onClick={() => onChange(m.key)}>{m.label}</button>
        ))}
      </div>
    );
  }

  renderLegendRow(label, count, pct, color, extra) {
    return (
      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: 'inline-block', flexShrink: 0 }} />
        <span style={{ color: COLORS.muted || '#8b91a0', flex: 1 }}>{label}{extra ? ` (${extra})` : ''}</span>
        <strong>{count}</strong>
        <span style={{ color: COLORS.muted || '#8b91a0', minWidth: 44, textAlign: 'right' }}>{pct}%</span>
      </div>
    );
  }

  // "Share of Business" is a % composition table (Organized % + Unorganized %
  // = 100% per month, and for the Total column) - confirmed against the
  // actual Power BI report, not a table of raw counts/weights per month.
  buildShareOfBusinessGrid(shareOfBusiness, cellValueFn = (items) => items.length) {
    const rowTypes = ['Organized', 'Unorganized'];
    const monthTotals = {};
    shareOfBusiness.months.forEach((m) => {
      monthTotals[m] = sum(rowTypes.map((type) => cellValueFn(shareOfBusiness.cell(type, m))));
    });
    const grandTotalValue = sum(rowTypes.map((type) => shareOfBusiness.rowTotals[type]));

    const records = rowTypes.map((type) => {
      const row = { transporterType: type };
      shareOfBusiness.months.forEach((m) => {
        const value = cellValueFn(shareOfBusiness.cell(type, m));
        row[m] = monthTotals[m] ? round1((value / monthTotals[m]) * 100) : 0;
      });
      row.total = grandTotalValue ? round1((shareOfBusiness.rowTotals[type] / grandTotalValue) * 100) : 0;
      return row;
    });
    const columnDefs = [
      { headerName: 'Transporter Type', field: 'transporterType', pinned: 'left', width: 180 },
      ...shareOfBusiness.months.map((m) => ({ headerName: m, field: m, width: 110, valueFormatter: percentValueFormatter })),
      { headerName: 'Total', field: 'total', width: 110, pinned: 'right', cellClass: 'pdm-ag-strong', valueFormatter: percentValueFormatter },
    ];
    const pinnedBottomRowData = [{
      transporterType: 'Total',
      ...Object.fromEntries(shareOfBusiness.months.map((m) => [m, 100])),
      total: 100,
    }];
    return { columnDefs, records, pinnedBottomRowData };
  }

  renderLrWiseTab(d) {
    return (
      <>
        <div className="pdm-kpi-grid">
          <div className="pdm-kpi-card">
            <div className="pdm-kpi-label">Total LRs (period)</div>
            <div className="pdm-kpi-value">{d.totalLR}</div>
            <div className="pdm-kpi-sub">Current filters</div>
          </div>
        </div>

        <div className="pdm-section">
          <div className="pdm-grid-3">
            {this.renderDonutWithLegend(
              'Distribution of Movement Type',
              'Primary vs Secondary LR share.',
              d.movementTypeDist,
              (items) => this.buildDonutOptions(items),
              (item, color) => this.renderLegendRow(item.name, item.count, item.pct, color),
            )}
            {this.renderDonutWithLegend(
              'Mode Distribution',
              'PTL vs FTL LR share.',
              d.modeDist,
              (items) => this.buildDonutOptions(items),
              (item, color) => this.renderLegendRow(item.name, item.count, item.pct, color),
            )}
            {this.renderDonutWithLegend(
              'Transporter Category Distribution',
              'LR share by transporter category.',
              d.categoryDist,
              (items) => this.buildDonutOptions(items),
              (item, color) => this.renderLegendRow(item.name, item.count, item.pct, color, `${item.transporterCount} transporters`),
            )}
          </div>
        </div>

        <div className="pdm-section">
          <div className="pdm-card">
            <div className="pdm-card-toolbar" style={{ justifyContent: 'space-between' }}>
              <div>
                <p className="pdm-card-title" style={{ margin: 0 }}>LR Dispatched - Source City</p>
                <p className="pdm-card-subtitle" style={{ margin: '2px 0 0' }}>Click a city to drill into its transporter breakdown.</p>
              </div>
              {this.renderMeasureToggle(LR_MEASURE_OPTIONS, this.state.sourceCityMeasure, (key) => this.setState({ sourceCityMeasure: key }))}
            </div>
            <DrilldownChartCard
              rows={d.sourceCityRows}
              drilldownMap={d.sourceCityDrilldown}
              measureKey={this.state.sourceCityMeasure}
              yTitle={LR_MEASURE_OPTIONS.find((m) => m.key === this.state.sourceCityMeasure).label}
              height={340}
              buildOptions={this.buildDrilldownBarOptions}
            />
          </div>
        </div>

        <div className="pdm-section">
          <div className="pdm-card">
            <div className="pdm-card-toolbar" style={{ justifyContent: 'space-between' }}>
              <div>
                <p className="pdm-card-title" style={{ margin: 0 }}>LR Delivered - Destination Zone</p>
                <p className="pdm-card-subtitle" style={{ margin: '2px 0 0' }}>Click a zone to drill into its transporter breakdown.</p>
              </div>
              {this.renderMeasureToggle(LR_MEASURE_OPTIONS, this.state.destZoneMeasure, (key) => this.setState({ destZoneMeasure: key }))}
            </div>
            <DrilldownChartCard
              rows={d.destZoneRows}
              drilldownMap={d.destZoneDrilldown}
              measureKey={this.state.destZoneMeasure}
              yTitle={LR_MEASURE_OPTIONS.find((m) => m.key === this.state.destZoneMeasure).label}
              height={340}
              buildOptions={this.buildDrilldownBarOptions}
            />
          </div>
        </div>

        <div className="pdm-section">
          <div className="pdm-section-head">
            <div>
              <h2>LR Wise Share of Business</h2>
              <p>Organized (API-tracked) vs Unorganized transporters, by month.</p>
            </div>
          </div>
          <GridCard title="LR Wise Share of Business" loading={this.state.loading} onRefresh={this.loadData} {...this.buildShareOfBusinessGrid(d.shareOfBusiness)} />
        </div>
      </>
    );
  }

  renderInvoiceWiseTab(d) {
    return (
      <>
        <div className="pdm-kpi-grid">
          <div className="pdm-kpi-card">
            <div className="pdm-kpi-label">Total Invoices (period)</div>
            <div className="pdm-kpi-value">{d.totalInvoices}</div>
            <div className="pdm-kpi-sub">Current filters</div>
          </div>
        </div>

        <div className="pdm-section">
          <div className="pdm-grid-3">
            {this.renderDonutWithLegend(
              'Distribution of Movement Type',
              'Primary vs Secondary invoice share.',
              d.invoiceMovementTypeDist,
              (items) => this.buildDonutOptions(items, 'Total Invoices', 'Invoices'),
              (item, color) => this.renderLegendRow(item.name, item.count, item.pct, color),
            )}
            {this.renderDonutWithLegend(
              'Mode Distribution',
              'PTL vs FTL invoice share.',
              d.invoiceModeDist,
              (items) => this.buildDonutOptions(items, 'Total Invoices', 'Invoices'),
              (item, color) => this.renderLegendRow(item.name, item.count, item.pct, color),
            )}
            {this.renderDonutWithLegend(
              'Transporter Category Distribution',
              'Invoice share by transporter category.',
              d.invoiceCategoryDist,
              (items) => this.buildDonutOptions(items, 'Total Invoices', 'Invoices'),
              (item, color) => this.renderLegendRow(item.name, item.count, item.pct, color, `${item.transporterCount} transporters`),
            )}
          </div>
        </div>

        <div className="pdm-section">
          <div className="pdm-card">
            <p className="pdm-card-title">Invoice Dispatched - Source City</p>
            <p className="pdm-card-subtitle">Click a city to drill into its transporter breakdown. Invoice Count only - this feed has no weight field.</p>
            <DrilldownChartCard
              rows={d.invoiceSourceCityRows}
              drilldownMap={d.invoiceSourceCityDrilldown}
              measureKey="invoiceCount"
              yTitle="Invoice Count"
              height={340}
              buildOptions={this.buildDrilldownBarOptions}
            />
          </div>
        </div>

        <div className="pdm-section">
          <div className="pdm-card">
            <p className="pdm-card-title">Invoice Delivered - Destination Zone</p>
            <p className="pdm-card-subtitle">Click a zone to drill into its transporter breakdown. Invoice Count only - this feed has no weight field.</p>
            <DrilldownChartCard
              rows={d.invoiceDestZoneRows}
              drilldownMap={d.invoiceDestZoneDrilldown}
              measureKey="invoiceCount"
              yTitle="Invoice Count"
              height={340}
              buildOptions={this.buildDrilldownBarOptions}
            />
          </div>
        </div>

        <div className="pdm-section">
          <div className="pdm-section-head">
            <div>
              <h2>Invoice Wise Share of Business</h2>
              <p>Organized (API-tracked) vs Unorganized transporters, by month - counted by distinct invoice.</p>
            </div>
          </div>
          <GridCard title="Invoice Wise Share of Business" loading={this.state.invoiceLoading} onRefresh={this.loadInvoiceData} {...this.buildShareOfBusinessGrid(d.invoiceShareOfBusiness)} />
        </div>
      </>
    );
  }

  renderWeightWiseTab(d) {
    return (
      <>
        <div className="pdm-kpi-grid">
          <div
            className={`pdm-kpi-card pdm-kpi-card-clickable ${this.state.statusScope === 'all' ? 'pdm-kpi-card-active' : ''}`}
            onClick={() => this.setStatusScope('all')}
            title="Click to show all statuses across the widgets below"
          >
            <div className="pdm-kpi-label">Total Weight Dispatched, Tons (period)</div>
            <div className="pdm-kpi-value">{d.totalWeightTons}</div>
            <div className="pdm-kpi-sub">All statuses - click to show all statuses below</div>
          </div>
          <div
            className={`pdm-kpi-card pdm-kpi-card-clickable ${this.state.statusScope === 'delivered' ? 'pdm-kpi-card-active' : ''}`}
            onClick={() => this.setStatusScope('delivered')}
            title="Click to scope the widgets below to delivered shipments only"
          >
            <div className="pdm-kpi-label">Total Weight Delivered, Tons (period)</div>
            <div className="pdm-kpi-value">{d.totalWeightDeliveredTons}</div>
            <div className="pdm-kpi-sub">Delivered only - click to scope widgets below to delivered</div>
          </div>
        </div>

        <div className="pdm-section">
          <div className="pdm-grid-3">
            {this.renderDonutWithLegend(
              'Distribution of Movement Type (Tons)',
              'Primary vs Secondary total weight share.',
              d.weightMovementTypeDist,
              (items) => this.buildDonutOptions(items, 'Total Weight (Tons)', 'Tons'),
              (item, color) => this.renderLegendRow(item.name, item.count, item.pct, color),
            )}
            {this.renderDonutWithLegend(
              'Mode Distribution (Tons)',
              'PTL vs FTL total weight share.',
              d.weightModeDist,
              (items) => this.buildDonutOptions(items, 'Total Weight (Tons)', 'Tons'),
              (item, color) => this.renderLegendRow(item.name, item.count, item.pct, color),
            )}
            {this.renderDonutWithLegend(
              'Transporter Category Distribution (Tons)',
              'Total weight share by transporter category.',
              d.weightCategoryDist,
              (items) => this.buildDonutOptions(items, 'Total Weight (Tons)', 'Tons'),
              (item, color) => this.renderLegendRow(item.name, item.count, item.pct, color, `${item.transporterCount} transporters`),
            )}
          </div>
        </div>

        <div className="pdm-section">
          <div className="pdm-card">
            <p className="pdm-card-title">Weight (Tons) Dispatched - Source City</p>
            <p className="pdm-card-subtitle">LR-wise. Click a city to drill into its transporter breakdown.</p>
            <DrilldownChartCard
              rows={d.weightSourceCityRows}
              drilldownMap={d.weightSourceCityDrilldown}
              measureKey="weightTons"
              yTitle="Total Weight (Tons)"
              height={340}
              buildOptions={this.buildDrilldownBarOptions}
            />
          </div>
        </div>

        <div className="pdm-section">
          <div className="pdm-card">
            <p className="pdm-card-title">Weight (Tons) Delivered - Destination Zone</p>
            <p className="pdm-card-subtitle">LR-wise. Click a zone to drill into its transporter breakdown.</p>
            <DrilldownChartCard
              rows={d.destZoneRows}
              drilldownMap={d.destZoneDrilldown}
              measureKey="weightTons"
              yTitle="Total Weight (Tons)"
              height={340}
              buildOptions={this.buildDrilldownBarOptions}
            />
          </div>
        </div>

        <div className="pdm-section">
          <div className="pdm-section-head">
            <div>
              <h2>Weight Wise Share of Business</h2>
              <p>Organized (API-tracked) vs Unorganized transporters, by month - total weight (tons).</p>
            </div>
          </div>
          <GridCard
            title="Weight Wise Share of Business"
            loading={this.state.loading}
            onRefresh={this.loadData}
            {...this.buildShareOfBusinessGrid(d.weightShareOfBusiness, (items) => round1(sum(items.map(weightTonsOf))))}
          />
        </div>
      </>
    );
  }

  render() {
    const { state } = this;
    const f = state.filters;
    const fo = state.filterOptions;
    const d = state.derived;

    return (
      <>
        {state.loading && (
          <div className="pdm-loading-overlay"><div className="pdm-spinner" /></div>
        )}
        {state.error && <div className="pdm-error-banner">{state.error}</div>}

        <div className="pdm-filterbar">
          <div className="pdm-field">
            <label className="pdm-filter-required">From</label>
            <DatePicker className="pdm-datepicker" selected={f.startDate} onChange={(v) => this.onFilterChange('startDate', v)} dateFormat="dd/MM/yyyy" maxDate={f.endDate} />
          </div>
          <div className="pdm-field">
            <label className="pdm-filter-required">To</label>
            <DatePicker className="pdm-datepicker" selected={f.endDate} onChange={(v) => this.onFilterChange('endDate', v)} dateFormat="dd/MM/yyyy" minDate={f.startDate} maxDate={new Date()} />
          </div>
          <div className="pdm-field">
            <label>Date Basis</label>
            <Select styles={MULTI_SELECT_STYLES} value={f.dateType} options={DATE_TYPE_OPTIONS} onChange={(v) => this.onFilterChange('dateType', v)} />
          </div>
          <div className="pdm-field">
            <label>Mode</label>
            <Select styles={MULTI_SELECT_STYLES} value={f.mode} options={MODE_OPTIONS} onChange={(v) => this.onFilterChange('mode', v)} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Entity</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.entity} options={fo.entity} onChange={(v) => this.onFilterChange('entity', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Movement Type</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.movementType} options={fo.movementType} onChange={(v) => this.onFilterChange('movementType', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Source Zone</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.sourceZone} options={fo.sourceZone} onChange={(v) => this.onFilterChange('sourceZone', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Source City</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.sourceCity} options={fo.sourceCity} onChange={(v) => this.onFilterChange('sourceCity', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Destination Zone</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.destinationZone} options={fo.destinationZone} onChange={(v) => this.onFilterChange('destinationZone', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Destination City</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.destinationCity} options={fo.destinationCity} onChange={(v) => this.onFilterChange('destinationCity', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Destination State</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.destinationState} options={fo.destinationState} onChange={(v) => this.onFilterChange('destinationState', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Fuel Type</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.fuelType} options={fo.fuelType} onChange={(v) => this.onFilterChange('fuelType', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Transporter Category</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.transporterCategory} options={fo.transporterCategory} onChange={(v) => this.onFilterChange('transporterCategory', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Invoice Type</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.invoiceType} options={fo.invoiceType} onChange={(v) => this.onFilterChange('invoiceType', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Transporter</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.transporter} options={fo.transporter} onChange={(v) => this.onFilterChange('transporter', v || [])} />
          </div>
          <div className="pdm-filter-toolbar">
            <button className="pdm-btn pdm-btn-ghost" onClick={this.onReset}>Reset</button>
            <button className="pdm-btn pdm-btn-primary" onClick={this.onApply}>Apply Filters</button>
          </div>
        </div>

        <div className="pdm-card-toolbar" style={{ marginBottom: 14 }}>
          {DATA_VIEW_TABS.map((tab) => (
            <button
              key={tab.key}
              className={`pdm-chip ${state.activeDataView === tab.key ? 'pdm-chip-active' : ''}`}
              onClick={() => this.setState({ activeDataView: tab.key })}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {state.activeDataView === 'lr' ? (
          !d ? (
            <div className="pdm-card">{this.renderEmptyState('Loading dashboard...', null)}</div>
          ) : this.renderLrWiseTab(d)
        ) : state.activeDataView === 'invoice' ? (
          state.invoiceError ? (
            <div className="pdm-card">{this.renderEmptyState('Unable to load invoice-wise data', state.invoiceError)}</div>
          ) : !state.invoiceDerived ? (
            <div className="pdm-card">{this.renderEmptyState(state.invoiceLoading ? 'Loading invoice-wise data...' : 'No invoice data available', null)}</div>
          ) : this.renderInvoiceWiseTab(state.invoiceDerived)
        ) : (
          !d ? (
            <div className="pdm-card">{this.renderEmptyState('Loading dashboard...', null)}</div>
          ) : this.renderWeightWiseTab(d)
        )}
      </>
    );
  }
}
